import React, { useEffect, useState } from "react";
import styles from "../multipleSubMenu.module.css";
import myStyle from "./cim-sub-menu.module.css";
import cn from "classnames";
import { useFloating, autoUpdate, offset, flip, shift, FloatingPortal } from "@floating-ui/react";
import { PortalID } from "../modal/portal-types";
import { widgets_orgChartIconImage } from "../canvas-designer-new/sidebar/side-menu-items/side-menu-icons";
import { MondayLogo } from "frontend/icons/icons";
import useCanvasTemplates from "../hooks/use-canvas-templates";
import { CursorType } from "../canvas-designer-new/cursor-type";
import { CimWidgetsMenuTabBar } from "../ui-components/design-system/tab-bar/tab-bar-better";
import { CloseIcon } from "frontend/icons/close";
import PadInline from "./components/pad-inline";
import WidgetsSubMenu from "./widgets/widgets-sub-menu";
import TemplatesSubMenu from "./templates/templates-sub-menu";
import SyncedWidgetsSubMenu from "./synced-widgets/synced-widgets-sub-menu";
import { getPathPrefix } from "utils/getPathPrefix";
import tracking from "frontend/tracking";

const enum WidgetType {
  Widgets,
  SyncedWidgets,
  Templates,
}

const TAB_NAMES = {
  [WidgetType.Widgets]: "Widgets",
  [WidgetType.SyncedWidgets]: "Synced Widgets",
  [WidgetType.Templates]: "Templates",
};

type SelectedWidget =
  | { type: "widget"; widget: CursorType }
  | { type: "template"; template: any }
  | { type: "liveIntegration"; data: any };

export function CimSubMenu({
  relativeTo,
  onSelected,
  onDismiss,
}: {
  relativeTo: React.RefObject<HTMLElement>;
  onSelected: (selected: SelectedWidget) => void;
  onDismiss: () => void;
}) {
  const portal = document.getElementById(PortalID.Floaters);
  const [selected, setSelected] = useState<WidgetType>(WidgetType.Widgets);

  const { templates } = useCanvasTemplates();
  // Set up floating UI
  const { refs, floatingStyles } = useFloating({
    open: !!relativeTo.current,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const floatingElement = refs.floating.current;

      if (floatingElement && floatingElement.contains(target)) {
        return;
      }

      const isInsideFloatingDropdown = target.closest('[data-floating-dropdown="true"]');
      if (isInsideFloatingDropdown) {
        return;
      }

      onDismiss();
    };

    document.addEventListener("mouseup", handleClickOutside);
    return () => {
      document.removeEventListener("mouseup", handleClickOutside);
    };
  }, [refs.floating, onDismiss]);

  useEffect(() => {
    if (relativeTo.current) {
      refs.setReference(relativeTo.current);
    }
  }, [relativeTo, refs]);

  if (!relativeTo.current || !portal) return null;

  return (
    <FloatingPortal id={PortalID.Floaters}>
      <div ref={refs.setFloating} style={floatingStyles}>
        <div
          className={cn(styles.floater, styles.colorPicker)}
          style={{
            paddingBlock: "13px",
            height: "430px",
            maxHeight: "72.5vh",
            width: "360px",
            borderRadius: "15px",
            background: "#fff",
            boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.05)",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #EBEDF3",
          }}
        >
          <PadInline>
            <CimWidgetsMenuTabBar
              tabBarStyle={{
                color: "#1E1E1E",
                height: "unset",
                display: "flex",
                justifyContent: "start",
                gap: "10px",
                padding: "5px 0",
              }}
              gliderStyle={{
                backgroundColor: "#F6F7FB",
              }}
              initialSelected={selected}
              onTabChange={(index) => {
                tracking.trackAnalyticsEvent("cim_sub_menu_tab_changed", {
                  tab: TAB_NAMES[index as WidgetType],
                });
                setSelected(index);
              }}
            >
              <span>{TAB_NAMES[WidgetType.Widgets]}</span>
              <span>
                <MondayLogo width={15} /> Synced Widgets
              </span>
              <span>Templates</span>
            </CimWidgetsMenuTabBar>
          </PadInline>
          {selected == WidgetType.Widgets && (
            <WidgetsSubMenu
              onClick={(widget) => {
                if (widget === CursorType.orgChart) {
                  // There's a specific template we prefer for org charts, but if I can't find it, I'll look for the first template with org chart in the name.
                  let template = templates?.find((t) => t.id == process.env.ORGCHART_TEMPLATE_ID_CIM_WIDGETS_SUBMENU);
                  if (!template) {
                    const rx = /org\s?chart/i;
                    template = templates?.find((t) => rx.test(t.name));
                  }
                  if (!template) {
                    onSelected({ type: "widget", widget });
                  } else {
                    if (/org\s?chart/i.test(template.name)) {
                      // for org charts template we show a different icon
                      onSelected({
                        type: "template",
                        template: { ...template, thumbnailImageURL: widgets_orgChartIconImage },
                      });
                    } else {
                      onSelected({
                        type: "template",
                        template,
                      });
                    }
                  }
                } else {
                  onSelected({ type: "widget", widget });
                }
              }}
            />
          )}
          {selected == WidgetType.SyncedWidgets && (
            <SyncedWidgetsSubMenu
              onClick={(widget) => onSelected({ type: "widget", widget })}
              onClickTemplate={(templateIdOrData: string | any) => {
                if (templateIdOrData === null) {
                  onDismiss();
                  return;
                }
                if (Array.isArray(templateIdOrData)) {
                  // Org chart data - comes as an array with full element
                  if (templateIdOrData[0]) {
                    const orgChartData = {
                      alias: "org-chart-integration",
                      thumbnailImageURL: getPathPrefix("/images/template-thumbnails/smart-org-chart.png"),
                      ...templateIdOrData[0],
                    };
                    onSelected({
                      type: "template",
                      template: orgChartData,
                    });
                  }
                } else if (typeof templateIdOrData === "object" && templateIdOrData.integrationId) {
                  // Card stack integration data - comes as an object with integrationId
                  onSelected({
                    type: "liveIntegration",
                    data: templateIdOrData,
                  });
                } else if (typeof templateIdOrData === "string") {
                  const template = templates?.find((t) => t.id === templateIdOrData);
                  if (!template) return;
                  onSelected({ type: "template", template });
                }
              }}
            />
          )}
          {selected == WidgetType.Templates && (
            <TemplatesSubMenu
              templates={templates}
              onClick={(template) => onSelected({ type: "template", template })}
            />
          )}

          <button
            className={myStyle.buttonReset}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              width: "20px",
              height: "20px",
              display: "grid",
              zIndex: 1001,
              placeContent: "center",
            }}
            onClick={onDismiss}
          >
            <CloseIcon color="#848199" />
          </button>
        </div>
      </div>
    </FloatingPortal>
  );
}
